import { FC, useMemo } from "react";
import { ColumnDef } from "@tanstack/react-table";

import { Button } from "@/components/button";
import { Table } from "@/components/table/table";
import { UuidFieldRow } from "@/components/table/uuid-field-row";
import { TextFieldRow } from "@/components/table/text-field-row";
import { NumberFieldRow } from "@/components/table/number-field-row";
import { DateTimeFieldRow } from "@/components/table/datetime-field-row";
import { NoteSummarySchema } from "@/domains/openapi/types";

interface NotesTableProps {
  data: NoteSummarySchema[];
  pageSize: number;
  onViewNote: (id: string) => void;
  onLoadMore?: () => void;
  showLoadMore?: boolean;
  loadMoreLabel?: string;
  isLoadMoreDisabled?: boolean;
}

export const NotesTable: FC<NotesTableProps> = ({
  data,
  pageSize,
  onViewNote,
  onLoadMore,
  showLoadMore,
  loadMoreLabel,
  isLoadMoreDisabled,
}) => {
  const columns = useMemo<ColumnDef<NoteSummarySchema>[]>(
    () => [
      {
        header: "Note ID",
        cell: ({ row }) => <UuidFieldRow id={row.original.id} />,
      },
      {
        header: "Owner Space Account",
        cell: ({ row }) =>
          row.original.owned_by_space_account_id ? (
            <UuidFieldRow id={row.original.owned_by_space_account_id} />
          ) : (
            <TextFieldRow value="-" />
          ),
      },
      {
        header: "Actions",
        cell: ({ row }) => (
          <Button size="1" onClick={() => onViewNote(row.original.id)}>
            View
          </Button>
        ),
      },
      {
        header: "Primary Label",
        cell: ({ row }) => <TextFieldRow value={row.original.primary_label} />,
      },
      {
        header: "Secondary Label",
        cell: ({ row }) => <TextFieldRow value={row.original.secondary_label ?? "-"} />,
      },
      {
        header: "Version",
        cell: ({ row }) => <NumberFieldRow value={row.original.version} />,
      },
      {
        header: "Updated At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.locally_modified_at} />,
      },
      {
        header: "Created At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.locally_created_at} />,
      },
      {
        header: "Trashed At",
        cell: ({ row }) => <DateTimeFieldRow dateStr={row.original.trashed_at} />,
      },
    ],
    [onViewNote]
  );

  return (
    <Table
      data={data}
      columns={columns}
      pageSize={pageSize}
      onLoadMore={onLoadMore}
      showLoadMore={showLoadMore}
      loadMoreLabel={loadMoreLabel}
      isLoadMoreDisabled={isLoadMoreDisabled}
    />
  );
};
